import React, { useState, useEffect, useRef, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head } from "lodash";
import { isNil } from "lodash";
import { i18n } from "../../translate/i18n";
import moment from "moment-timezone";

import api from "../../services/api";
import axios from "axios";
import toastError from "../../errors/toastError";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import Chip from "@material-ui/core/Chip";
import UserStatusIcon from "../UserModal/statusIcon";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import useQueues from "../../hooks/useQueues";
import DistributionConfigurator from "../DistributionConfigurator";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

// Schema de validação – campos essenciais e obrigatórios
const CampaignSchema = Yup.object()
  .shape({
    name: Yup.string()
      .min(2, "Muito curto!")
      .max(50, "Muito longo!")
      .required("Nome da campanha é obrigatório"),
    scheduledAt: Yup.date()
      .min(new Date(), "Agendamento deve ser para o futuro")
      .required("Data e hora são obrigatórios"),
    whatsappIds: Yup.string().required("Selecione uma conexão"),
    openTicket: Yup.string().required("Opção de abrir ticket é obrigatória"),
    // Quando a confirmação estiver habilitada, sempre exigir a mensagem de confirmação da Msg. 1
    confirmationMessage1: Yup.string().when("confirmation", {
      is: true,
      then: Yup.string().required(
        "Mensagem de confirmação para Msg. 1 é obrigatória"
      ),
      otherwise: Yup.string(),
    }),
    // Para Msg. 2 a 5, se a mensagem for preenchida e a confirmação estiver habilitada, a confirmação é obrigatória
    confirmationMessage2: Yup.string().when(["confirmation", "message2"], {
      is: (confirmation, message2) =>
        confirmation && message2 && message2.trim() !== "",
      then: Yup.string().required(
        "Mensagem de confirmação para Msg. 2 é obrigatória"
      ),
      otherwise: Yup.string(),
    }),
    confirmationMessage3: Yup.string().when(["confirmation", "message3"], {
      is: (confirmation, message3) =>
        confirmation && message3 && message3.trim() !== "",
      then: Yup.string().required(
        "Mensagem de confirmação para Msg. 3 é obrigatória"
      ),
      otherwise: Yup.string(),
    }),
    confirmationMessage4: Yup.string().when(["confirmation", "message4"], {
      is: (confirmation, message4) =>
        confirmation && message4 && message4.trim() !== "",
      then: Yup.string().required(
        "Mensagem de confirmação para Msg. 4 é obrigatória"
      ),
      otherwise: Yup.string(),
    }),
    confirmationMessage5: Yup.string().when(["confirmation", "message5"], {
      is: (confirmation, message5) =>
        confirmation && message5 && message5.trim() !== "",
      then: Yup.string().required(
        "Mensagem de confirmação para Msg. 5 é obrigatória"
      ),
      otherwise: Yup.string(),
    }),
  })
  .test("contactOrTag", "Selecione uma Lista de Contato ou uma Tag", (values) =>
    Boolean(
      (values.contactListId && values.contactListId.length > 0) || (values.tagListId && values.tagListId.length > 0)
    )
  );

const CampaignModal = ({
  open,
  onClose,
  campaignId,
  initialValues,
  onSave,
  resetPagination,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMounted = useRef(true);
  const cancelSourceRef = useRef();
  const { user } = useContext(AuthContext);
  const { companyId } = user;

  // Inicializa scheduledAt para a data atual + 5 minutos
  const initialState = {
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    confirmationMessage1: "",
    confirmationMessage2: "",
    confirmationMessage3: "",
    confirmationMessage4: "",
    confirmationMessage5: "",
    status: "INATIVA", // INATIVA, PROGRAMADA, EM_ANDAMENTO, CANCELADA, FINALIZADA
    confirmation: false,
    scheduledAt: moment().add(5, "minutes").format("YYYY-MM-DDTHH:mm"),
    contactListId: [],
    tagListId: [],
    companyId,
    statusTicket: "closed",
    openTicket: "disabled",
    whatsappIds: "",
    audioFirst: false, // false = mensagem primeiro, true = áudio primeiro
    randomization: {
      isEnabled: false,
      randomizationType: "connection",
      distributionRules: []
    },
    // Configurações específicas da campanha
    useGlobalSettings: true,
    // Configurações de Intervalo
    messageInterval: 20,
    longerIntervalAfter: 20,
    greaterInterval: 60
  };

  const [campaign, setCampaign] = useState({
    ...initialState,
    contactListId: [],
    tagListId: []
  });
  const [whatsapps, setWhatsapps] = useState([]);
  const [whatsappsForSending, setWhatsappsForSending] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [tagLists, setTagLists] = useState([]);
  const [messageTab, setMessageTab] = useState(0);
  const [attachment, setAttachment] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const attachmentFile = useRef(null);

  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [randomizationEnabled, setRandomizationEnabled] = useState(false);
  const [randomizationType, setRandomizationType] = useState("connection");
  const [distributionRules, setDistributionRules] = useState([]);
  const { findAll: findAllQueues } = useQueues();

  // Funções para gerenciar randomização
  const addDistributionRule = () => {
    setDistributionRules([...distributionRules, { whatsappId: "", userId: "", percentage: 0 }]);
  };

  const removeDistributionRule = (index) => {
    const newRules = distributionRules.filter((_, i) => i !== index);
    setDistributionRules(newRules);
  };

  const updateDistributionRule = (index, field, value) => {
    const newRules = [...distributionRules];
    newRules[index][field] = value;
    setDistributionRules(newRules);
  };

  const getTotalPercentage = () => {
    return distributionRules.reduce((total, rule) => total + (parseInt(rule.percentage) || 0), 0);
  };

  // Adicionar regra inicial quando randomização for ativada
  useEffect(() => {
    if (randomizationEnabled && distributionRules.length === 0) {
      addDistributionRule();
    }
  }, [randomizationEnabled, randomizationType]);

  useEffect(() => {
    cancelSourceRef.current = axios.CancelToken.source();
    return () => {
      isMounted.current = false;
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel("unmount");
      }
    };
  }, []);

  useEffect(() => {
    if (!isMounted.current) return;
    const loadQueues = async () => {
      const list = await findAllQueues();
      if (!isMounted.current) return;
      setAllQueues(list);
      setQueues(list);
    };
    loadQueues();
  }, []);

  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/");
          if (!isMounted.current) return;
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          if (!isMounted.current) return;
          setLoading(false);
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    if (!isMounted.current) return;
    const source = cancelSourceRef.current;
    const loadAll = async () => {
      try {
        if (initialValues) {
          const localScheduledAt = initialValues.scheduledAt
            ? moment(initialValues.scheduledAt, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DDTHH:mm")
            : null;
          if (isMounted.current) {
            setCampaign((prevState) => ({
              ...prevState,
              ...initialValues,
              scheduledAt: localScheduledAt || prevState.scheduledAt
            }));
          }
        }

        const contactListsResp = await api.get(`/contact-lists/list`, { params: { companyId }, cancelToken: source.token });
        if (isMounted.current) {
          setContactLists(contactListsResp.data);
        }

        const whatsappsResp = await api.get(`/whatsapp`, { params: { companyId, session: 0, forDistribution: true }, cancelToken: source.token });
        const mappedWhatsapps = whatsappsResp.data.map((whatsapp) => ({
          ...whatsapp,
          selected: false,
        }));
        if (isMounted.current) {
          setWhatsapps(mappedWhatsapps);
          const whatsappsForSending = user.profile === "admin"
            ? mappedWhatsapps
            : mappedWhatsapps.filter((w) => w.id === user.whatsappId);
          setWhatsappsForSending(whatsappsForSending);
        }

        const usersResp = await api.get("/users/", { params: { companyId }, cancelToken: source.token });
        if (isMounted.current) {
          setOptions(usersResp.data.users);
        }

        const tagsResp = await api.get(`/tags/list`, { params: { companyId, kanban: 0 }, cancelToken: source.token });
        const formattedTagLists = tagsResp.data.map((tag) => ({
          id: tag.id,
          name: tag.name,
          color: tag.color,
        }));
        if (isMounted.current) {
          setTagLists(formattedTagLists);
        }

        if (!campaignId) return;
        const campResp = await api.get(`/campaigns/${campaignId}`, { cancelToken: source.token });
        const data = campResp.data;
        if (!isMounted.current) return;
        if (data?.user) setSelectedUser(data.user);
        if (data?.queue) setSelectedQueue(data.queue.id);
        if (data?.whatsappId) {
          setCampaign((prev) => ({ ...prev, whatsappIds: data.whatsappId }));
        }
        if (!isMounted.current) return;
        setCampaign((prev) => {
          let prevCampaignData = { ...prev };
          Object.entries(data).forEach(([key, value]) => {
            if (key === "scheduledAt" && value) {
              const localTime = moment.utc(value).local().format("YYYY-MM-DDTHH:mm");
              prevCampaignData[key] = localTime;
            } else if (key === "contactList" && Array.isArray(value)) {
              prevCampaignData.contactListId = value.map(cl => cl.id);
            } else if (key === "contactListId") {
              if (Array.isArray(value)) {
                prevCampaignData.contactListId = value;
              } else if (value && typeof value === 'number') {
                prevCampaignData.contactListId = [value];
              } else {
                prevCampaignData.contactListId = [];
              }
            } else if (key === "tagListId") {
              if (Array.isArray(value)) {
                prevCampaignData.tagListId = value;
              } else if (value && (typeof value === 'number' || typeof value === 'string')) {
                prevCampaignData.tagListId = [value];
              } else {
                prevCampaignData.tagListId = [];
              }
            } else {
              prevCampaignData[key] = value === null ? "" : value;
            }
          });
          if (!Array.isArray(prevCampaignData.contactListId)) {
            prevCampaignData.contactListId = [];
          }
          if (!Array.isArray(prevCampaignData.tagListId)) {
            prevCampaignData.tagListId = [];
          }
          return prevCampaignData;
        });
      } catch (err) {
        if (axios.isCancel(err)) return;
        toastError(err);
      }
    };
    loadAll();
  }, [campaignId, open, initialValues, companyId]);

  useEffect(() => {
    const now = moment();
    const scheduledAt = moment(campaign.scheduledAt);
    const moreThenAnHour =
      !Number.isNaN(scheduledAt.diff(now)) && scheduledAt.diff(now, "hour") > 1;
    const isEditable =
      campaign.status === "INATIVA" ||
      (campaign.status === "PROGRAMADA" && moreThenAnHour);
    setCampaignEditable(isEditable);
  }, [campaign.status, campaign.scheduledAt]);

  const handleClose = () => {
    setCampaign(initialState);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = e.target.files[0];
    setAttachment(file);
  };

  // Função para verificar se o anexo/mídia é um arquivo de áudio
  const isAudioFile = () => {
    if (attachment) {
      return attachment.type && attachment.type.startsWith('audio/');
    }
    if (campaign.mediaName) {
      const ext = campaign.mediaName.toLowerCase();
      return ext.endsWith('.mp3') || ext.endsWith('.ogg') || ext.endsWith('.wav') || ext.endsWith('.m4a');
    }
    return false;
  };

  const handleSaveCampaign = async (values) => {
    // Valida se foi selecionada uma Lista de Contato ou uma Tag
    if (
      !(
        (values.contactListId && values.contactListId.length > 0) ||
        (values.tagListId && values.tagListId.length > 0)
      )
    ) {
      toast.error("Selecione uma Lista de Contato ou uma Tag.");
      return;
    }
    // Valida se foi informada ao menos uma mensagem ou anexado um arquivo
    if (!values.message1 && !attachment) {
      toast.error("É necessário informar uma mensagem ou anexar um arquivo.");
      return;
    }

    // Valida distribuição aleatória se estiver habilitada
    if (randomizationEnabled) {
      if (getTotalPercentage() !== 100) {
        toast.error("A soma das porcentagens de distribuição deve ser exatamente 100%.");
        return;
      }

      const hasValidRules = distributionRules.filter(rule => {
        const hasDestination = randomizationType === "connection" 
          ? rule.whatsappId 
          : rule.userId;
        return hasDestination && rule.percentage > 0;
      }).length > 0;

      if (!hasValidRules) {
        toast.error(`Configure pelo menos um${randomizationType === "connection" ? "a conexão" : " usuário"} para distribuição.`);
        return;
      }
    }
    try {
      const dataValues = {
        ...values,
        whatsappId: values.whatsappIds,
        userId: selectedUser?.id || null,
        queueId: selectedQueue || null,
        status: "INATIVA",
        userId: user.id,
      };

      console.log(`[Frontend] Processando values:`, values);
      
      Object.entries(values).forEach(([key, value]) => {
        if (key === "scheduledAt" && value) {
          // FIX: Input datetime-local retorna "YYYY-MM-DDTHH:mm" sem timezone
          // Precisamos interpretá-lo como horário local e converter para UTC
          console.log(`[Frontend ENVIO] Campo ${key} - Valor original: ${value}`);
          console.log(`[Frontend ENVIO] Timezone atual: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
          
          // Interpretar explicitamente como horário local
          const localMoment = moment.tz(value, "YYYY-MM-DDTHH:mm", "America/Sao_Paulo");
          const utcTime = localMoment.utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
          dataValues[key] = utcTime;
          console.log(`[Frontend ENVIO] Local moment: ${localMoment.format()}`);
          console.log(`[Frontend ENVIO] Campo ${key} - Convertido para UTC: ${utcTime}`);
        } else {
          dataValues[key] = value === "" ? null : value;
        }
      });

      // Adicionar dados de randomização se habilitado
      if (randomizationEnabled) {
        dataValues.randomization = {
          isEnabled: randomizationEnabled,
          randomizationType: randomizationType,
          distributionRules: distributionRules
            .filter(rule => {
              const hasDestination = randomizationType === "connection" 
                ? rule.whatsappId 
                : rule.userId;
              return hasDestination && rule.percentage > 0;
            })
            .map(rule => ({
              percentage: rule.percentage,
              ...(randomizationType === "connection" 
                ? { whatsappId: rule.whatsappId }
                : { userId: rule.userId }
              )
            }))
        };
      }

      if (campaignId) {
        await api.put(`/campaigns/${campaignId}`, dataValues);
        if (attachment) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${campaignId}/media-upload`, formData);
        }
        handleClose();
      } else {
        const { data } = await api.post("/campaigns", dataValues);
        if (attachment) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${data.id}/media-upload`, formData);
        }
        if (onSave) onSave(data);
        handleClose();
      }
      toast.success(i18n.t("campaigns.toasts.success"));
    } catch (err) {
      console.log(err);
      toastError(err);
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }
    if (campaign.mediaPath) {
      await api.delete(`/campaigns/${campaign.id}/media-upload`);
      setCampaign((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("campaigns.toasts.deleted"));
    }
  };

  // Agora os campos exibem a mensagem de erro se a validação falhar
  const renderMessageField = (identifier, errors, touched) => (
    <Field
      as={TextField}
      id={identifier}
      name={identifier}
      fullWidth
      rows={5}
      label={i18n.t(`campaigns.dialog.form.${identifier}`)}
      placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
      multiline
      variant="outlined"
      helperText={
        touched[identifier] && errors[identifier]
          ? errors[identifier]
          : "Utilize variáveis como {nome}, {numero}, {email} ou defina variáveis personalizadas."
      }
      error={touched[identifier] && Boolean(errors[identifier])}
      disabled={!campaignEditable && campaign.status !== "CANCELADA"}
    />
  );

  const renderConfirmationMessageField = (identifier, errors, touched) => (
    <Field
      as={TextField}
      id={identifier}
      name={identifier}
      fullWidth
      rows={5}
      label={i18n.t(`campaigns.dialog.form.${identifier}`)}
      placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
      multiline
      variant="outlined"
      error={touched[identifier] && Boolean(errors[identifier])}
      helperText={touched[identifier] && errors[identifier]}
      disabled={!campaignEditable && campaign.status !== "CANCELADA"}
    />
  );

  const cancelCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filterOptions = createFilterOptions({ trim: true });

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {campaignEditable
            ? campaignId
              ? i18n.t("campaigns.dialog.update")
              : i18n.t("campaigns.dialog.new")
            : i18n.t("campaigns.dialog.readonly")}
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={handleAttachmentFile}
          />
        </div>
        <Formik
          initialValues={{
            ...campaign,
            contactListId: Array.isArray(campaign.contactListId) ? campaign.contactListId : [],
            tagListId: Array.isArray(campaign.tagListId) ? campaign.tagListId : []
          }}
          enableReinitialize
          validationSchema={CampaignSchema}
          onSubmit={(values, actions) => {
            handleSaveCampaign(values);
            actions.setSubmitting(false);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  {/* Nome da campanha */}
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.name")}
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>
                  {/* Confirmação */}
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="confirmation-selection-label">
                        {i18n.t("campaigns.dialog.form.confirmation")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.confirmation")}
                        name="confirmation"
                        labelId="confirmation-selection-label"
                        disabled={!campaignEditable}
                      >
                        <MenuItem value={false}>Desabilitada</MenuItem>
                        <MenuItem value={true}>Habilitada</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  {/* Lista de Contato */}
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="contactList-selection-label">
                        {i18n.t("campaigns.dialog.form.contactList")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.contactList")}
                        name="contactListId"
                        labelId="contactList-selection-label"
                        error={
                          touched.contactListId && Boolean(errors.contactListId)
                        }
                        disabled={!campaignEditable}
                        multiple
                        onChange={(e) => {
                          setFieldValue("contactListId", e.target.value);
                          if (e.target.value && e.target.value.length > 0) {
                            // Se selecionar uma lista de contato, desmarca a tag
                            setFieldValue("tagListId", []);
                          }
                        }}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          getContentAnchorEl: null,
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              marginTop: 5,
                            },
                          },
                        }}
                        renderValue={(selected) => {
                          // Garantir que selected seja sempre um array
                          const selectedArray = Array.isArray(selected) ? selected : [];
                          return (
                            <div style={{ display: "flex", flexWrap: "wrap" }}>
                              {selectedArray.map((value) => {
                                const contactList = contactLists.find((cl) => cl.id === value);
                                return (
                                  <Chip
                                    key={value}
                                    label={contactList ? contactList.name : value}
                                    variant="outlined"
                                    style={{
                                      backgroundColor: "#1976d2",
                                      color: "#FFF",
                                      marginRight: 4,
                                      padding: "1px 2px",
                                      fontWeight: "bold",
                                      textShadow: "1px 1px 1px rgba(0,0,0,0.5)",
                                      borderRadius: 15,
                                      fontSize: "0.75rem",
                                      whiteSpace: "nowrap",
                                      height: 20,
                                      border: "none",
                                    }}
                                    size="small"
                                  />
                                );
                              })}
                            </div>
                          );
                        }}
                      >
                        {contactLists.map((contactList) => (
                          <MenuItem key={contactList.id} value={contactList.id}>
                            {contactList.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  {/* Tag */}
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="tagList-selection-label">
                        {i18n.t("campaigns.dialog.form.tagList")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.tagList")}
                        name="tagListId"
                        labelId="tagList-selection-label"
                        error={touched.tagListId && Boolean(errors.tagListId)}
                        disabled={!campaignEditable}
                        multiple
                        onChange={(e) => {
                          setFieldValue("tagListId", e.target.value);
                          if (e.target.value && e.target.value.length > 0) {
                            // Se selecionar uma tag, desmarca a lista de contato
                            setFieldValue("contactListId", []);
                          }
                        }}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          getContentAnchorEl: null,
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              marginTop: 5,
                            },
                          },
                        }}
                        renderValue={(selected) => {
                          // Garantir que selected seja sempre um array
                          const selectedArray = Array.isArray(selected) ? selected : [];
                          return (
                            <div style={{ display: "flex", flexWrap: "wrap" }}>
                              {selectedArray.map((value) => {
                                const tag = tagLists.find((t) => t.id === value);
                                return (
                                  <Chip
                                    key={value}
                                    label={tag ? tag.name : value}
                                    variant="outlined"
                                    style={{
                                      backgroundColor: tag?.color || "#eee",
                                      color: "#FFF",
                                      marginRight: 4,
                                      padding: "1px 2px",
                                      fontWeight: "bold",
                                      textShadow: "1px 1px 1px rgba(0,0,0,0.5)",
                                      borderRadius: 15,
                                      fontSize: "0.75rem",
                                      whiteSpace: "nowrap",
                                      height: 20,
                                      border: "none",
                                    }}
                                    size="small"
                                  />
                                );
                              })}
                            </div>
                          );
                        }}
                      >
                        {tagLists.map((tagList) => (
                          <MenuItem key={tagList.id} value={tagList.id}>
                            <div
                              style={{
                                backgroundColor: tagList.color || "#eee",
                                color: "#fff",
                                padding: "2px 8px",
                                borderRadius: "15px",
                                fontSize: "0.75rem",
                                fontWeight: "bold",
                                display: "inline-block",
                                maxWidth: "100%",
                                textShadow: "1px 1px 1px rgba(0,0,0,0.5)",
                              }}
                            >
                              {tagList.name}
                            </div>
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  {/* Conexão (Whatsapp) */}
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="whatsapp-selection-label">
                        {i18n.t("campaigns.dialog.form.whatsapp")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.whatsapp")}
                        name="whatsappIds"
                        labelId="whatsapp-selection-label"
                        error={
                          touched.whatsappIds && Boolean(errors.whatsappIds)
                        }
                        disabled={!campaignEditable}
                        onChange={(e) =>
                          setFieldValue("whatsappIds", e.target.value)
                        }
                      >
                        {whatsappsForSending.map((whatsapp) => (
                          <MenuItem key={whatsapp.id} value={whatsapp.id}>
                            {whatsapp.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  {/* Abrir Ticket */}
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="openTicket-selection-label">
                        {i18n.t("campaigns.dialog.form.openTicket")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.openTicket")}
                        name="openTicket"
                        labelId="openTicket-selection-label"
                        error={touched.openTicket && Boolean(errors.openTicket)}
                        disabled={!campaignEditable}
                      >
                        <MenuItem value={"enabled"}>
                          {i18n.t("campaigns.dialog.form.enabledOpenTicket")}
                        </MenuItem>
                        <MenuItem value={"disabled"}>
                          {i18n.t("campaigns.dialog.form.disabledOpenTicket")}
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  {/* Data/Hora de Agendamento */}
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.scheduledAt")}
                      name="scheduledAt"
                      type="datetime-local"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={touched.scheduledAt && Boolean(errors.scheduledAt)}
                      helperText={touched.scheduledAt && errors.scheduledAt}
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>
                  {/* Outros campos (Usuário, Fila, etc.) */}
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      style={{ marginTop: "8px" }}
                      variant="outlined"
                      margin="dense"
                      className={classes.formControl}
                      getOptionLabel={(option) => option.name}
                      value={selectedUser}
                      size="small"
                      onChange={(e, newValue) => {
                        setSelectedUser(newValue);
                        if (newValue && Array.isArray(newValue.queues)) {
                          if (newValue.queues.length === 1) {
                            setSelectedQueue(newValue.queues[0].id);
                          }
                          setQueues(newValue.queues);
                        } else {
                          setQueues(allQueues);
                          setSelectedQueue("");
                        }
                      }}
                      options={options}
                      filterOptions={filterOptions}
                      freeSolo
                      fullWidth
                      autoHighlight
                      disabled={
                        !campaignEditable || values.openTicket === "disabled"
                      }
                      noOptionsText={i18n.t("transferTicketModal.noOptions")}
                      loading={loading}
                      renderOption={(option) => (
                        <span>
                          <UserStatusIcon user={option} /> {option.name}
                        </span>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={i18n.t("transferTicketModal.fieldLabel")}
                          variant="outlined"
                          onChange={(e) => setSearchParam(e.target.value)}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>
                        {i18n.t("transferTicketModal.fieldQueueLabel")}
                      </InputLabel>
                      <Select
                        value={selectedQueue}
                        onChange={(e) => setSelectedQueue(e.target.value)}
                        label={i18n.t(
                          "transferTicketModal.fieldQueuePlaceholder"
                        )}
                        required={!isNil(selectedUser)}
                        disabled={
                          !campaignEditable || values.openTicket === "disabled"
                        }
                      >
                        {queues.map((queue) => (
                          <MenuItem key={queue.id} value={queue.id}>
                            {queue.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="statusTicket-selection-label">
                        {i18n.t("campaigns.dialog.form.statusTicket")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.statusTicket")}
                        name="statusTicket"
                        labelId="statusTicket-selection-label"
                        error={
                          touched.statusTicket && Boolean(errors.statusTicket)
                        }
                        disabled={
                          !campaignEditable || values.openTicket === "disabled"
                        }
                      >
                        <MenuItem value={"closed"}>
                          {i18n.t("campaigns.dialog.form.closedTicketStatus")}
                        </MenuItem>
                        <MenuItem value={"pending"}>
                          {i18n.t("campaigns.dialog.form.pendingTicketStatus")}
                        </MenuItem>
                        <MenuItem value={"open"}>
                          {i18n.t("campaigns.dialog.form.openTicketStatus")}
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  
                  {/* Seção de Configurações */}
                  <Grid item xs={12}>
                    <Divider style={{ margin: "20px 0" }} />
                    <Typography variant="h6" style={{ marginBottom: "16px", color: "#1976d2" }}>
                      Configurações da Campanha
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Field name="useGlobalSettings">
                          {({ field, form }) => (
                            <Switch
                              checked={field.value}
                              onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                              disabled={!campaignEditable}
                              color="primary"
                            />
                          )}
                        </Field>
                      }
                      label="Usar configurações globais da empresa"
                    />
                  </Grid>

                  {!values.useGlobalSettings && (
                    <>
                      {/* Configurações de Intervalo */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" style={{ marginTop: "16px", marginBottom: "8px" }}>
                          Configurações de Intervalo
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel>Intervalo randômico de disparo</InputLabel>
                          <Field
                            as={Select}
                            name="messageInterval"
                            label="Intervalo randômico de disparo"
                            disabled={!campaignEditable}
                          >
                            <MenuItem value={0}>Sem Intervalo</MenuItem>
                            <MenuItem value={5}>5 segundos</MenuItem>
                            <MenuItem value={10}>10 segundos</MenuItem>
                            <MenuItem value={15}>15 segundos</MenuItem>
                            <MenuItem value={20}>20 segundos</MenuItem>
                            <MenuItem value={30}>30 segundos</MenuItem>
                            <MenuItem value={40}>40 segundos</MenuItem>
                            <MenuItem value={60}>60 segundos</MenuItem>
                            <MenuItem value={70}>70 segundos</MenuItem>
                            <MenuItem value={80}>80 segundos</MenuItem>
                            <MenuItem value={100}>100 segundos</MenuItem>
                            <MenuItem value={120}>120 segundos</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel>Intervalo maior após</InputLabel>
                          <Field
                            as={Select}
                            name="longerIntervalAfter"
                            label="Intervalo maior após"
                            disabled={!campaignEditable}
                          >
                            <MenuItem value={0}>Não definido</MenuItem>
                            <MenuItem value={5}>5 mensagens</MenuItem>
                            <MenuItem value={10}>10 mensagens</MenuItem>
                            <MenuItem value={15}>15 mensagens</MenuItem>
                            <MenuItem value={20}>20 mensagens</MenuItem>
                            <MenuItem value={30}>30 mensagens</MenuItem>
                            <MenuItem value={40}>40 mensagens</MenuItem>
                            <MenuItem value={50}>50 mensagens</MenuItem>
                            <MenuItem value={60}>60 mensagens</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel>Intervalo de disparo maior</InputLabel>
                          <Field
                            as={Select}
                            name="greaterInterval"
                            label="Intervalo de disparo maior"
                            disabled={!campaignEditable}
                          >
                            <MenuItem value={0}>Sem Intervalo</MenuItem>
                            <MenuItem value={20}>20 segundos</MenuItem>
                            <MenuItem value={30}>30 segundos</MenuItem>
                            <MenuItem value={40}>40 segundos</MenuItem>
                            <MenuItem value={50}>50 segundos</MenuItem>
                            <MenuItem value={60}>60 segundos</MenuItem>
                            <MenuItem value={70}>70 segundos</MenuItem>
                            <MenuItem value={80}>80 segundos</MenuItem>
                            <MenuItem value={90}>90 segundos</MenuItem>
                            <MenuItem value={100}>100 segundos</MenuItem>
                            <MenuItem value={110}>110 segundos</MenuItem>
                            <MenuItem value={120}>120 segundos</MenuItem>
                            <MenuItem value={130}>130 segundos</MenuItem>
                            <MenuItem value={140}>140 segundos</MenuItem>
                            <MenuItem value={150}>150 segundos</MenuItem>
                            <MenuItem value={160}>160 segundos</MenuItem>
                            <MenuItem value={170}>170 segundos</MenuItem>
                            <MenuItem value={180}>180 segundos</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>
                    </>
                  )}

                  {/* Seção de Randomização */}
                  <Grid item xs={12}>
                    <Divider style={{ margin: "20px 0" }} />
                    <Typography variant="h6" style={{ marginBottom: "16px", color: "#1976d2" }}>
                      Configurações de Distribuição Aleatória
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={randomizationEnabled}
                          onChange={(e) => setRandomizationEnabled(e.target.checked)}
                          disabled={!campaignEditable}
                        />
                      }
                      label="Ativar distribuição aleatória"
                    />
                  </Grid>
                  
                  {randomizationEnabled && (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel>Tipo de Randomização</InputLabel>
                          <Select
                            value={randomizationType}
                            onChange={(e) => setRandomizationType(e.target.value)}
                            disabled={!campaignEditable}
                          >
                            <MenuItem value="connection">Por Conexão</MenuItem>
                            <MenuItem value="user">Por Usuário</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <DistributionConfigurator
                        distributionRules={distributionRules}
                        whatsapps={whatsapps}
                        users={options}
                        randomizationType={randomizationType}
                        onUpdateRule={updateDistributionRule}
                        onAddRule={addDistributionRule}
                        onRemoveRule={removeDistributionRule}
                        campaignEditable={campaignEditable}
                        totalPercentage={getTotalPercentage()}
                      />
                    </>
                  )}
                  
                  <Grid item xs={12}>
                    <Tabs
                      value={messageTab}
                      indicatorColor="primary"
                      textColor="primary"
                      onChange={(e, v) => setMessageTab(v)}
                      variant="fullWidth"
                      centered
                      style={{
                        background: "#f2f2f2",
                        border: "1px solid #e6e6e6",
                        borderRadius: 2,
                      }}
                    >
                      <Tab label="Msg. 1" index={0} />
                      <Tab label="Msg. 2" index={1} />
                      <Tab label="Msg. 3" index={2} />
                      <Tab label="Msg. 4" index={3} />
                      <Tab label="Msg. 5" index={4} />
                    </Tabs>
                                         <div style={{ paddingTop: 20, border: "none" }}>
                       {messageTab === 0 &&
                         (values.confirmation ? (
                           <Grid container spacing={2}>
                             <Grid item xs={12} md={8}>
                               {renderMessageField("message1", errors, touched)}
                             </Grid>
                             <Grid item xs={12} md={4}>
                               {renderConfirmationMessageField(
                                 "confirmationMessage1",
                                 errors,
                                 touched
                               )}
                             </Grid>
                           </Grid>
                         ) : (
                           renderMessageField("message1", errors, touched)
                         ))}
                       {messageTab === 1 &&
                         (values.confirmation ? (
                           <Grid container spacing={2}>
                             <Grid item xs={12} md={8}>
                               {renderMessageField("message2", errors, touched)}
                             </Grid>
                             <Grid item xs={12} md={4}>
                               {renderConfirmationMessageField(
                                 "confirmationMessage2",
                                 errors,
                                 touched
                               )}
                             </Grid>
                           </Grid>
                         ) : (
                           renderMessageField("message2", errors, touched)
                         ))}
                       {messageTab === 2 &&
                         (values.confirmation ? (
                           <Grid container spacing={2}>
                             <Grid item xs={12} md={8}>
                               {renderMessageField("message3", errors, touched)}
                             </Grid>
                             <Grid item xs={12} md={4}>
                               {renderConfirmationMessageField(
                                 "confirmationMessage3",
                                 errors,
                                 touched
                               )}
                             </Grid>
                           </Grid>
                         ) : (
                           renderMessageField("message3", errors, touched)
                         ))}
                       {messageTab === 3 &&
                         (values.confirmation ? (
                           <Grid container spacing={2}>
                             <Grid item xs={12} md={8}>
                               {renderMessageField("message4", errors, touched)}
                             </Grid>
                             <Grid item xs={12} md={4}>
                               {renderConfirmationMessageField(
                                 "confirmationMessage4",
                                 errors,
                                 touched
                               )}
                             </Grid>
                           </Grid>
                         ) : (
                           renderMessageField("message4", errors, touched)
                         ))}
                       {messageTab === 4 &&
                         (values.confirmation ? (
                           <Grid container spacing={2}>
                             <Grid item xs={12} md={8}>
                               {renderMessageField("message5", errors, touched)}
                             </Grid>
                             <Grid item xs={12} md={4}>
                               {renderConfirmationMessageField(
                                 "confirmationMessage5",
                                 errors,
                                 touched
                               )}
                             </Grid>
                           </Grid>
                         ) : (
                           renderMessageField("message5", errors, touched)
                         ))}
                     </div>
                  </Grid>
                  {(campaign.mediaPath || attachment) && (
                    <Grid item xs={12}>
                                             <div style={{ display: "flex", alignItems: "center" }}>
                         <Button startIcon={<AttachFileIcon />}>
                           {attachment ? attachment.name : campaign.mediaName}
                         </Button>
                         
                         {/* Toggle para ordem do áudio - só aparece se for arquivo de áudio */}
                         {isAudioFile() && campaignEditable && (
                           <div style={{ display: "flex", alignItems: "center", marginLeft: 16 }}>
                             <Typography variant="body2">
                               Áudio primeiro?
                             </Typography>
                             <Field name="audioFirst">
                               {({ field, form }) => (
                                 <Switch
                                   checked={field.value}
                                   onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                                   color="primary"
                                   size="small"
                                 />
                               )}
                             </Field>
                           </div>
                         )}
                         
                         {campaignEditable && (
                           <IconButton
                             onClick={() => setConfirmationOpen(true)}
                             color="primary"
                           >
                             <DeleteOutlineIcon color="secondary" />
                           </IconButton>
                         )}
                       </div>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                {campaign.status === "CANCELADA" && (
                  <Button
                    color="primary"
                    onClick={restartCampaign}
                    variant="outlined"
                  >
                    {i18n.t("campaigns.dialog.buttons.restart")}
                  </Button>
                )}
                {campaign.status === "EM_ANDAMENTO" && (
                  <Button
                    color="primary"
                    onClick={cancelCampaign}
                    variant="outlined"
                  >
                    {i18n.t("campaigns.dialog.buttons.cancel")}
                  </Button>
                )}
                {!attachment && !campaign.mediaPath && campaignEditable && (
                  <Button
                    color="primary"
                    onClick={() => attachmentFile.current.click()}
                    disabled={isSubmitting}
                    variant="outlined"
                  >
                    {i18n.t("campaigns.dialog.buttons.attach")}
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  color="primary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("campaigns.dialog.buttons.close")}
                </Button>
                {(campaignEditable || campaign.status === "CANCELADA") && (
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {campaignId
                      ? i18n.t("campaigns.dialog.buttons.edit")
                      : i18n.t("campaigns.dialog.buttons.add")}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CampaignModal;
